// ============================================================
//  VERSION CONTROL PAGE — ADMIN DASHBOARD
//  Path: src/pages/VersionControlPage.tsx
//
//  Two sections on one page:
//    1. App Versions  — controls force-update / soft-prompt
//       thresholds for iOS and Android apps.
//    2. Policy Versions — controls the version number and
//       effective date displayed on policy screens.
//
//  Access: super_admin + admin only.
//
//  FIX (review pass):
//  - Raising the minimum version saved on one click. A typo (2.0.0
//    instead of 1.2.0) blocks EVERY user of that app behind a
//    force-update screen with no update to install. Saving a higher
//    minimum now asks for confirmation, names the version that must
//    already be live in the App Store / Play Store, and warns when it
//    is above the latest version.
//  - Minimum above latest was accepted; it's rejected now.
//  - Changing a policy version makes every user re-accept; it now
//    asks first, and flags an unchanged effective date.
//  - After a save the inputs kept the typed text (" 1.2.3 " stayed
//    "unsaved" forever) — they now resync from the saved value.
//  - A failed load showed empty sections with no retry; now an error
//    with Try again. Server messages (e.g. a 403) are shown.
//  - Overlapping refreshes (two rows saved at once) can't apply an
//    older response over a newer one.
//
//  FIX (second review pass — app versions):
//  - An app type with no database row simply didn't appear, and there
//    was no way to create it — so e.g. customer_android could never be
//    given a minimum version from here. All four mobile app types are
//    now always shown; a missing one is marked "Not configured" and
//    the first Save creates it (the backend upserts).
//  - Unknown app types (indexOf === -1) sorted to the TOP of the list;
//    they now sort after the known ones. Web rows, if present, are
//    labelled.
//  - Inputs are compared in canonical form ("1.2" == "1.2.0"), the
//    same form the backend now stores, and the row shows what will
//    actually be saved when it differs from what was typed.
//  - The "✓ Saved" timer is cleared on unmount / next save.
//  - Server-side 409 (someone else saved the same row at once) is
//    shown and the row is refreshed.
//
//  BUILD NUMBERS (new):
//  - Each app row now also has Minimum build / Latest build. EAS raises
//    the build number by itself on every build, so pushing an update no
//    longer needs a version string to be bumped. Leave both empty to use
//    only the version strings (what the app did before).
//  - Same rules as the versions: raising the minimum build asks for
//    confirmation (it blocks every phone below it), and the minimum can't
//    be higher than the latest. A minimum with no latest is refused here.
//  - Phones on a build older than the one that introduced this feature
//    ignore build numbers and keep using the version strings.
//
//  SAFETY GUARDS (new):
//  - Live check while typing: Minimum Version can never be higher than
//    Latest Version, Minimum Build can never be higher than Latest
//    Build, and a Minimum Build needs a Latest Build. The boxes turn
//    red, the reason is shown, and Save is disabled until it is fixed.
//    (The backend refuses the same cases too — this just stops it
//    before the request.)
//  - RED BANNER whenever a version is changed: the app's own version
//    lives in app.config.js and this page cannot change it. The banner
//    says what app.config.js must contain and that a build with that
//    version must already be live in the store before saving.
// ============================================================

import React, { useEffect, useRef, useState } from 'react'
import {
  listAppVersions, updateAppVersion,
  listPolicyVersions, updatePolicyVersion,
} from '../services/admin.service'

// ── Types ─────────────────────────────────────────────────────
interface AppVersion {
  id:             string | null   // null = no row yet (placeholder)
  appType:        string
  minimumVersion: string
  latestVersion:  string
  minimumBuild:   number | null
  latestBuild:    number | null
  updatedAt:      string | null
  updatedByUid:   string | null
}

interface PolicyVersion {
  id:            string
  policyType:    string
  version:       string
  effectiveDate: string
  updatedAt:     string
  updatedByUid:  string | null
}

// ── Display labels ────────────────────────────────────────────
const APP_TYPE_LABELS: Record<string, string> = {
  customer_ios:     '🍎 Customer — iOS',
  customer_android: '🤖 Customer — Android',
  expert_ios:       '🍎 Expert — iOS',
  expert_android:   '🤖 Expert — Android',
  customer_web:     '🌐 Customer — Web',
  expert_web:       '🌐 Expert — Web',
}

// Always shown, even before the backend has a row for them.
const MOBILE_APP_TYPES = ['customer_ios', 'customer_android', 'expert_ios', 'expert_android']
const APP_TYPE_ORDER   = [...MOBILE_APP_TYPES, 'customer_web', 'expert_web']

const placeholderRow = (appType: string): AppVersion => ({
  id: null, appType, minimumVersion: '', latestVersion: '',
  minimumBuild: null, latestBuild: null, updatedAt: null, updatedByUid: null,
})

// Known types in a fixed order; anything unexpected goes last.
const orderIndex = (list: string[], key: string) => {
  const i = list.indexOf(key)
  return i === -1 ? list.length : i
}

const POLICY_TYPE_LABELS: Record<string, string> = {
  expert_terms:               '📄 Expert Terms & Conditions',
  customer_terms:             '📄 Customer Terms & Conditions',
  privacy_policy:             '🔒 Privacy Policy',
  prohibited_services_policy: '🚫 Prohibited Services Policy',
  delivery_agreement:         '🚚 Delivery Agreement',
  delivery_handling_ack:      '📦 Delivery Handling Acknowledgement',
}

// ── Helpers ───────────────────────────────────────────────────
const isValidVersion = (v: unknown): boolean =>
  typeof v === 'string' && /^\d{1,3}(\.\d{1,3}){0,2}$/.test(v.trim())

// Same canonical form the backend stores: "1.2" → "1.2.0",
// "01.2.3" → "1.2.3". Only call with strings that pass isValidVersion.
const normalizeVersion = (v: string): string => {
  const parts = v.trim().split('.').map((n) => String(parseInt(n, 10)))
  while (parts.length < 3) parts.push('0')
  return parts.join('.')
}

// Canonical if valid, otherwise trimmed as typed — for dirty checks.
const canon = (v: string) => (isValidVersion(v) ? normalizeVersion(v) : v.trim())

// Numeric, part by part; missing parts count as 0 ("1.2" == "1.2.0").
// Only call with strings that pass isValidVersion.
const compareVersions = (a: string, b: string): number => {
  const pa = a.trim().split('.').map(Number)
  const pb = b.trim().split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

// Build-number inputs are kept as text so an empty box means "off".
const buildStr   = (n: number | null | undefined) => (typeof n === 'number' ? String(n) : '')
const isValidBuild = (v: string): boolean => /^\d{1,9}$/.test(v.trim())
const toBuild    = (v: string): number | null => (v.trim() === '' ? null : parseInt(v.trim(), 10))

const fmt = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

const STORE_NAME = (appType: string) =>
  appType.endsWith('_ios') ? 'the App Store' : appType.endsWith('_android') ? 'Google Play' : 'the store'

const saveErrorMessage = (err: any) =>
  err?.response?.data?.message ||
  err?.response?.data?.errors?.join?.(', ') ||
  (!err?.response ? 'Could not reach the server — nothing was saved. Try again.' : 'Failed to save')

// Shows "✓ Saved" for 2s; the timer is cleared if the row unmounts
// or saves again before it fires.
const useSavedFlash = () => {
  const [success, setSuccess] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  const flash = () => {
    if (timer.current) clearTimeout(timer.current)
    setSuccess(true)
    timer.current = setTimeout(() => setSuccess(false), 2000)
  }
  const clear = () => {
    if (timer.current) clearTimeout(timer.current)
    setSuccess(false)
  }
  return { success, flash, clear }
}

// ── App Version Row ───────────────────────────────────────────
const AppVersionRow: React.FC<{
  row: AppVersion
  onSaved: () => void
}> = ({ row, onSaved }) => {
  const [minVer,  setMinVer]  = useState(row.minimumVersion)
  const [latVer,  setLatVer]  = useState(row.latestVersion)
  const [minBuild, setMinBuild] = useState(buildStr(row.minimumBuild))
  const [latBuild, setLatBuild] = useState(buildStr(row.latestBuild))
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const { success, flash, clear } = useSavedFlash()

  const isNew = row.id === null

  // Resync the inputs whenever the saved values change (after our own
  // save, or a refresh that brings someone else's change).
  useEffect(() => { setMinVer(row.minimumVersion) }, [row.minimumVersion])
  useEffect(() => { setLatVer(row.latestVersion) },  [row.latestVersion])
  useEffect(() => { setMinBuild(buildStr(row.minimumBuild)) }, [row.minimumBuild])
  useEffect(() => { setLatBuild(buildStr(row.latestBuild)) },  [row.latestBuild])

  const isDirty =
    canon(minVer) !== canon(row.minimumVersion) ||
    canon(latVer) !== canon(row.latestVersion)  ||
    minBuild.trim() !== buildStr(row.minimumBuild) ||
    latBuild.trim() !== buildStr(row.latestBuild)

  // What will actually be stored, when it differs from what was typed.
  const minPreview = isValidVersion(minVer) && normalizeVersion(minVer) !== minVer.trim() ? normalizeVersion(minVer) : null
  const latPreview = isValidVersion(latVer) && normalizeVersion(latVer) !== latVer.trim() ? normalizeVersion(latVer) : null

  // ── Live safety checks (see SAFETY GUARDS in the header) ──────
  const versionOrderBad =
    isValidVersion(minVer) && isValidVersion(latVer) &&
    compareVersions(normalizeVersion(minVer), normalizeVersion(latVer)) > 0
  const buildOrderBad =
    isValidBuild(minBuild) && isValidBuild(latBuild) &&
    toBuild(minBuild)! > toBuild(latBuild)!
  const buildNeedsLatest = isValidBuild(minBuild) && latBuild.trim() === ''
  const ruleError =
    versionOrderBad
      ? `Minimum version (${normalizeVersion(minVer)}) can't be higher than latest version (${normalizeVersion(latVer)}). Fix it before saving.`
    : buildOrderBad
      ? `Minimum build (${toBuild(minBuild)}) can't be higher than latest build (${toBuild(latBuild)}). Fix it before saving.`
    : buildNeedsLatest
      ? `A minimum build needs a latest build too — set Latest Build to ${toBuild(minBuild)} or higher.`
    : ''

  // A version was edited: the app itself must carry that version.
  const versionChanged =
    canon(minVer) !== canon(row.minimumVersion) ||
    canon(latVer) !== canon(row.latestVersion)
  const bannerVersion = isValidVersion(latVer) ? normalizeVersion(latVer) : latVer.trim()

  const handleSave = async () => {
    setError('')
    clear()
    if (ruleError) { setError(ruleError); return }
    if (!isValidVersion(minVer)) { setError('Minimum version format invalid (e.g. 1.2.3)'); return }
    if (!isValidVersion(latVer)) { setError('Latest version format invalid (e.g. 1.2.3)'); return }
    const newMin = normalizeVersion(minVer)
    const newLat = normalizeVersion(latVer)
    if (compareVersions(newMin, newLat) > 0) {
      setError(`Minimum version (${newMin}) can't be higher than latest version (${newLat}).`)
      return
    }
    if (minBuild.trim() !== '' && !isValidBuild(minBuild)) { setError('Minimum build must be a whole number (e.g. 11) or empty'); return }
    if (latBuild.trim() !== '' && !isValidBuild(latBuild)) { setError('Latest build must be a whole number (e.g. 11) or empty'); return }
    const newMinB = toBuild(minBuild)
    const newLatB = toBuild(latBuild)
    if (newMinB !== null && newLatB === null) {
      setError(`Set Latest build too (at least ${newMinB}) — a minimum build needs a latest build.`)
      return
    }
    if (newMinB !== null && newLatB !== null && newMinB > newLatB) {
      setError(`Minimum build (${newMinB}) can't be higher than latest build (${newLatB}).`)
      return
    }

    const label = APP_TYPE_LABELS[row.appType] || row.appType
    const store = STORE_NAME(row.appType)
    const oldMinValid = isValidVersion(row.minimumVersion)
    const oldLatValid = isValidVersion(row.latestVersion)
    const oldMin = oldMinValid ? normalizeVersion(row.minimumVersion) : '(not set)'
    const oldLat = oldLatValid ? normalizeVersion(row.latestVersion)  : '(not set)'
    // 0.0.0 blocks nobody, so setting it is never a lockout.
    const minBlocksAnyone = compareVersions(newMin, '0.0.0') > 0

    // Build-number changes (null = check off).
    const oldMinB = row.minimumBuild
    const oldLatB = row.latestBuild
    const buildForce = newMinB !== null && (oldMinB === null || newMinB > oldMinB)
    const buildSoft  = newLatB !== null && (oldLatB === null || newLatB > oldLatB)

    // Raising the minimum locks out everyone below it — confirm.
    if (buildForce && !(minBlocksAnyone && (!oldMinValid || compareVersions(newMin, row.minimumVersion) > 0))) {
      const ok = window.confirm(
        `FORCE UPDATE — ${label}\n\n` +
        `Minimum build: ${oldMinB ?? '(not set)'} → ${newMinB}\n\n` +
        `Everyone on a build below ${newMinB} will be BLOCKED from using the app until they update.\n` +
        `Build ${newMinB} must already be live in ${store}, or they'll have nothing to update to.` +
        (oldLatB !== null && newMinB! > oldLatB
          ? `\n\n⚠️ ${newMinB} is higher than the latest build saved before (${oldLatB}). Double-check it isn't a typo.`
          : '') +
        `\n\nContinue?`
      )
      if (!ok) return
    } else if (minBlocksAnyone && (!oldMinValid || compareVersions(newMin, row.minimumVersion) > 0)) {
      const aboveLatest = oldLatValid && compareVersions(newMin, row.latestVersion) > 0
      const ok = window.confirm(
        `FORCE UPDATE — ${label}\n\n` +
        `Minimum version: ${oldMin} → ${newMin}\n\n` +
        `Everyone on a version below ${newMin} will be BLOCKED from using the app until they update.\n` +
        `Version ${newMin} must already be live in ${store}, or they'll have nothing to update to.` +
        (aboveLatest
          ? `\n\n⚠️ ${newMin} is higher than the latest version saved before (${oldLat}). Double-check it isn't a typo.`
          : '') +
        `\n\nContinue?`
      )
      if (!ok) return
    } else if (buildSoft || !oldLatValid || compareVersions(newLat, row.latestVersion) > 0) {
      const ok = window.confirm(
        `${label}\n\n` +
        (!oldLatValid || compareVersions(newLat, row.latestVersion) > 0
          ? `Latest version: ${oldLat} → ${newLat}\n`
          : '') +
        (buildSoft ? `Latest build: ${oldLatB ?? '(not set)'} → ${newLatB}\n` : '') +
        `\nUsers on an older version or build will see an "update available" prompt. ` +
        `Make sure the new one is live in ${store}.\n\nContinue?`
      )
      if (!ok) return
    }

    setSaving(true)
    try {
      await updateAppVersion(row.appType, {
        minimumVersion: newMin,
        latestVersion:  newLat,
        minimumBuild:   newMinB,
        latestBuild:    newLatB,
      })
      flash()
      onSaved()
    } catch (err: any) {
      setError(saveErrorMessage(err))
      // 409: someone else saved this row at the same moment — show
      // their values so the admin decides again with current data.
      if (err?.response?.status === 409) onSaved()
    } finally {
      setSaving(false)
    }
  }

  const inputClass =
    'w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-brand-500 focus:outline-none font-mono'

  return (
    <div className={`border rounded-xl p-5 ${isNew ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-border-light bg-surface-tertiary/30'}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-white">
            {APP_TYPE_LABELS[row.appType] || row.appType}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {isNew
              ? 'Not configured — this app runs no update checks until you save.'
              : `Last updated ${fmt(row.updatedAt)}`}
          </p>
        </div>
        {success && (
          <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg">✓ Saved</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`min-${row.appType}`} className="block text-xs font-medium text-gray-400 mb-1.5">
            Minimum Version
            <span className="text-gray-600 ml-1">(force update if below)</span>
          </label>
          <input
            id={`min-${row.appType}`}
            value={minVer}
            onChange={(e) => { setMinVer(e.target.value); setError(''); clear() }}
            placeholder={isNew ? '0.0.0' : '1.0.0'}
            inputMode="decimal"
            autoComplete="off"
            className={inputClass + (versionOrderBad ? ' !border-red-500' : '')}
          />
          {minPreview && <p className="text-[11px] text-gray-500 mt-1">Saved as <span className="font-mono">{minPreview}</span></p>}
        </div>
        <div>
          <label htmlFor={`lat-${row.appType}`} className="block text-xs font-medium text-gray-400 mb-1.5">
            Latest Version
            <span className="text-gray-600 ml-1">(soft prompt if below)</span>
          </label>
          <input
            id={`lat-${row.appType}`}
            value={latVer}
            onChange={(e) => { setLatVer(e.target.value); setError(''); clear() }}
            placeholder="1.0.0"
            inputMode="decimal"
            autoComplete="off"
            className={inputClass + (versionOrderBad ? ' !border-red-500' : '')}
          />
          {latPreview && <p className="text-[11px] text-gray-500 mt-1">Saved as <span className="font-mono">{latPreview}</span></p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={`minb-${row.appType}`} className="block text-xs font-medium text-gray-400 mb-1.5">
            Minimum Build
            <span className="text-gray-600 ml-1">(force update if below)</span>
          </label>
          <input
            id={`minb-${row.appType}`}
            value={minBuild}
            onChange={(e) => { setMinBuild(e.target.value); setError(''); clear() }}
            placeholder="empty = off"
            inputMode="numeric"
            autoComplete="off"
            className={inputClass + (buildOrderBad || buildNeedsLatest ? ' !border-red-500' : '')}
          />
        </div>
        <div>
          <label htmlFor={`latb-${row.appType}`} className="block text-xs font-medium text-gray-400 mb-1.5">
            Latest Build
            <span className="text-gray-600 ml-1">(soft prompt if below)</span>
          </label>
          <input
            id={`latb-${row.appType}`}
            value={latBuild}
            onChange={(e) => { setLatBuild(e.target.value); setError(''); clear() }}
            placeholder="empty = off"
            inputMode="numeric"
            autoComplete="off"
            className={inputClass + (buildOrderBad ? ' !border-red-500' : '')}
          />
        </div>
      </div>

      {versionChanged && (
        <div className="border border-red-500/50 bg-red-500/10 rounded-lg px-3 py-2.5 mb-3 text-xs text-red-300 leading-relaxed" role="alert">
          <p className="font-semibold text-red-200 mb-1">⚠️ You changed a version — change the app too</p>
          <p>
            This page does not change the app. The version inside the app comes from{' '}
            <span className="font-mono">version</span> in <span className="font-mono">app.config.js</span>.
            Make sure it says <span className="font-mono font-semibold">{bannerVersion || '…'}</span> and that a build with
            that version is already live in {STORE_NAME(row.appType)} before you save. If the app still has an older version,
            people can be asked to update (or blocked) with nothing to install.
          </p>
        </div>
      )}

      {ruleError && <p className="text-red-400 text-xs mb-3 font-medium" role="alert">{ruleError}</p>}
      {error && !ruleError && <p className="text-red-400 text-xs mb-3" role="alert">{error}</p>}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">
          {isNew ? (
            'Tip: minimum 0.0.0 forces nobody.'
          ) : (
            <>
              Current: min <span className="text-gray-400 font-mono">{row.minimumVersion}</span>
              {' · '}latest <span className="text-gray-400 font-mono">{row.latestVersion}</span>
              {' · '}build <span className="text-gray-400 font-mono">{buildStr(row.minimumBuild) || '—'}</span>
              {' / '}<span className="text-gray-400 font-mono">{buildStr(row.latestBuild) || '—'}</span>
            </>
          )}
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !isDirty || !!ruleError}
          className="px-4 py-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-30 text-white rounded-lg transition-colors"
        >
          {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ── Policy Version Row ────────────────────────────────────────
const PolicyVersionRow: React.FC<{
  row: PolicyVersion
  onSaved: () => void
}> = ({ row, onSaved }) => {
  const [version,       setVersion]       = useState(row.version)
  const [effectiveDate, setEffectiveDate] = useState(row.effectiveDate)
  const [saving,        setSaving]        = useState(false)
  const [error,         setError]         = useState('')
  const { success, flash, clear } = useSavedFlash()

  useEffect(() => { setVersion(row.version) },             [row.version])
  useEffect(() => { setEffectiveDate(row.effectiveDate) }, [row.effectiveDate])

  const isDirty = version.trim() !== row.version || effectiveDate.trim() !== row.effectiveDate

  const handleSave = async () => {
    setError('')
    clear()
    const newVersion = version.trim()
    const newDate    = effectiveDate.trim()
    if (!newVersion) { setError('Version is required'); return }
    if (!newDate)    { setError('Effective date is required'); return }
    if (newVersion.length > 20) { setError('Version is too long (e.g. 1.1)'); return }
    if (newDate.length > 60)    { setError('Effective date is too long (e.g. August 13, 2026)'); return }

    const label = POLICY_TYPE_LABELS[row.policyType] || row.policyType
    if (newVersion !== row.version) {
      const ok = window.confirm(
        `${label}\n\nVersion: ${row.version} → ${newVersion}\nEffective date: ${newDate}\n\n` +
        `Every user will be asked to re-accept this policy the next time they open the app.` +
        (newDate === row.effectiveDate
          ? `\n\n⚠️ The effective date is unchanged (${newDate}). Update it too if the policy text changed.`
          : '') +
        `\n\nContinue?`
      )
      if (!ok) return
    }

    setSaving(true)
    try {
      await updatePolicyVersion(row.policyType, {
        version:       newVersion,
        effectiveDate: newDate,
      })
      flash()
      onSaved()
    } catch (err: any) {
      setError(saveErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border border-border-light rounded-xl p-5 bg-surface-tertiary/30">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-white">
            {POLICY_TYPE_LABELS[row.policyType] || row.policyType}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Last updated {fmt(row.updatedAt)}
          </p>
        </div>
        {success && (
          <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg">✓ Saved</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Version</label>
          <input
            value={version}
            onChange={(e) => { setVersion(e.target.value); setError(''); clear() }}
            placeholder="1.1"
            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-brand-500 focus:outline-none font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Effective Date</label>
          <input
            value={effectiveDate}
            onChange={(e) => { setEffectiveDate(e.target.value); setError(''); clear() }}
            placeholder="August 13, 2026"
            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-brand-500 focus:outline-none"
          />
        </div>
      </div>

      {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">
          Current: <span className="text-gray-400 font-mono">{row.version}</span>
          {' · '}
          <span className="text-gray-400">{row.effectiveDate}</span>
        </p>
        <button
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="px-4 py-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-30 text-white rounded-lg transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────
const VersionControlPage: React.FC = () => {
  const [appVersions,    setAppVersions]    = useState<AppVersion[]>([])
  const [policyVersions, setPolicyVersions] = useState<PolicyVersion[]>([])
  const [loading,        setLoading]        = useState(true)
  const [error,          setError]          = useState('')
  const [loaded,         setLoaded]         = useState(false)

  // Only the newest refresh may update the page.
  const requestSeq = useRef(0)

  const fetchAll = async () => {
    const seq = ++requestSeq.current
    setError('')
    try {
      const [appRes, policyRes] = await Promise.all([
        listAppVersions(),
        listPolicyVersions(),
      ])
      if (seq !== requestSeq.current) return
      setAppVersions(appRes.data?.data ?? [])
      setPolicyVersions(policyRes.data?.data ?? [])
      setLoaded(true)
    } catch (err: any) {
      if (seq !== requestSeq.current) return
      console.error('Failed to load version data:', err)
      setError(
        err?.response?.data?.message ||
        (!err?.response
          ? 'Could not reach the server — check your connection and try again.'
          : 'Failed to load version data')
      )
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  const retry = () => { setLoading(true); fetchAll() }

  useEffect(() => { fetchAll() }, [])

  // Every mobile app type is shown, with a placeholder where the
  // backend has no row yet; then any other rows (web, unknown).
  const byType = new Map(appVersions.map((r) => [r.appType, r]))
  const sortedAppVersions: AppVersion[] = [
    ...MOBILE_APP_TYPES.map((t) => byType.get(t) ?? placeholderRow(t)),
    ...appVersions.filter((r) => !MOBILE_APP_TYPES.includes(r.appType)),
  ].sort((a, b) => orderIndex(APP_TYPE_ORDER, a.appType) - orderIndex(APP_TYPE_ORDER, b.appType))

  // Sort policy versions in a consistent order
  const POLICY_TYPE_ORDER = [
    'expert_terms', 'customer_terms',
    'privacy_policy', 'prohibited_services_policy',
    'delivery_agreement', 'delivery_handling_ack',
  ]
  const sortedPolicyVersions = [...policyVersions].sort(
    (a, b) => orderIndex(POLICY_TYPE_ORDER, a.policyType) - orderIndex(POLICY_TYPE_ORDER, b.policyType)
  )

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Version Control</h1>
        <p className="text-gray-400 text-sm mt-1">
          Control app update gates and policy version numbers shown to users.
        </p>
      </div>

      {/* How it works — plain-English help with an example */}
      <details open className="mb-8 border border-border-light rounded-xl bg-surface-tertiary/30">
        <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-white">
          ℹ️ How the update screens work (read first)
        </summary>
        <div className="px-5 pb-5 text-sm text-gray-300 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-3">
              <p className="font-semibold text-red-300 mb-1">Minimum = FORCE screen</p>
              <p className="text-gray-400">
                Anyone BELOW the minimum is blocked until they update. The minimum itself is allowed.
              </p>
            </div>
            <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-3">
              <p className="font-semibold text-yellow-300 mb-1">Latest = SOFT prompt</p>
              <p className="text-gray-400">
                Anyone BELOW the latest sees a small Update Available window with Later and Update. They can keep using the app.
              </p>
            </div>
          </div>

          <div>
            <p className="font-semibold text-white mb-2">Example: Latest = 1.0.8</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-gray-400 text-left">
                    <th className="border border-border-light px-3 py-2 font-medium">Phone has</th>
                    <th className="border border-border-light px-3 py-2 font-medium">Minimum 1.0.6</th>
                    <th className="border border-border-light px-3 py-2 font-medium">Minimum 1.0.7</th>
                    <th className="border border-border-light px-3 py-2 font-medium">Minimum 1.0.8</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {[
                    ['1.0.5', 'F', 'F', 'F'],
                    ['1.0.6', 'S', 'F', 'F'],
                    ['1.0.7', 'S', 'S', 'F'],
                    ['1.0.8', '–', '–', '–'],
                  ].map((r) => (
                    <tr key={r[0]}>
                      <td className="border border-border-light px-3 py-2 text-white">{r[0]}</td>
                      {r.slice(1).map((c, i) => (
                        <td key={i} className={`border border-border-light px-3 py-2 ${
                          c === 'F' ? 'text-red-300 bg-red-500/10' : c === 'S' ? 'text-yellow-300 bg-yellow-500/10' : 'text-green-300'
                        }`}>
                          {c === 'F' ? 'Force screen' : c === 'S' ? 'Soft prompt' : 'Nothing'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Someone who installs the app fresh from the store gets the newest version, so they are already up to date and see nothing.
              Builds work the same way: a phone is forced if its version OR its build is below the minimum.
            </p>
          </div>

          <div>
            <p className="font-semibold text-white mb-1">Rules to stay safe</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-400">
              <li><span className="text-gray-200">Store first.</span> Upload the new build and wait until Google Play or the App Store really shows Update. Only then change numbers here.</li>
              <li><span className="text-gray-200">app.config.js follows Latest.</span> The version inside the app must equal the Latest Version. Minimum is only a rule here and never goes into the app.</li>
              <li><span className="text-gray-200">Minimum can never be higher than Latest</span> (versions or builds). Never set a number that is not live in the store yet, or people get blocked with nothing to install.</li>
              <li><span className="text-gray-200">Nothing here changes by itself.</span> This page never changes the app, and the app never changes this page.</li>
              <li><span className="text-gray-200">Written as three parts:</span> 1.0.7, not 1.07 or 1.7. They are different numbers.</li>
            </ol>
          </div>
        </div>
      </details>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">
            {error}
            {loaded && <span className="text-red-300/70"> — the values below may be out of date.</span>}
          </p>
          <button onClick={retry} disabled={loading}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0 disabled:opacity-50">
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : !loaded ? null : (
        <div className="space-y-10">

          {/* App Versions */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-white">📱 App Versions</h2>
              <p className="text-sm text-gray-400 mt-1">
                <span className="text-red-400 font-medium">Minimum version</span> — users on older versions see a force-update screen and cannot use the app until they update.{' '}
                <span className="text-yellow-400 font-medium">Latest version</span> — users on older versions see a dismissable "update available" prompt.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                <span className="text-gray-200 font-medium">Builds</span> work the same way but use the build number, which EAS raises
                by itself on every build — so you never have to change a version to push an update. Type the number of the build that
                is live in the store (Android: "Version code" in Play Console or on the EAS build page). Leave both empty to use only the
                versions. Phones on a build older than the one that added this feature ignore build numbers.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sortedAppVersions.map((row) => (
                <AppVersionRow key={row.appType} row={row} onSaved={fetchAll} />
              ))}
            </div>
          </section>

          {/* Divider */}
          <div className="border-t border-border-light" />

          {/* Policy Versions */}
          <section>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-white">📋 Policy Versions</h2>
              <p className="text-sm text-gray-400 mt-1">
                Version numbers and effective dates displayed on policy screens in the app.
                Updating the version here triggers re-acceptance the next time a user opens the app.
                <span className="text-yellow-400"> Remember to also update the policy text in the app code and ship a new build.</span>
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sortedPolicyVersions.map((row) => (
                <PolicyVersionRow key={row.id} row={row} onSaved={fetchAll} />
              ))}
            </div>
            {sortedPolicyVersions.length === 0 && (
              <p className="text-sm text-gray-500">No policy version rows found — the PolicyVersion table hasn't been seeded.</p>
            )}
          </section>

        </div>
      )}
    </div>
  )
}

export default VersionControlPage